<template>
  <v-card class="total-alarm rounded-xl card-bg card-bd h-100 d-flex flex-column" variant="elevated" elevation="0">
    <template #title>
      <div class="d-flex justify-space-between align-center">
        <span>昨日告警总数统计</span>
      </div>
    </template>
    <div class="d-flex flex-column ga-4 px-6 py-4 pt-0 flex-grow-1" style="min-height: 0">
      <!-- 告警总数统计 -->
      <div class="total-block">
        <div class="total-info d-flex align-center justify-space-between">
          <span class="text-grey-darken-1">昨日告警总数</span>
          <div class="d-flex align-baseline">
            <TransitionNumber :value="total" class="total-value" />
            <span class="text-grey-darken-1 ml-1">条</span>
          </div>
        </div>
        <div class="progress-bar mt-2">
          <div class="progress-fill" :style="{ width: percentage + '%' }"></div>
        </div>
      </div>

      <!-- 雷达扫描动画效果 (替代原版的 scan-block) -->
      <!-- 雷达扫描动画效果 (替代原版的 scan-block) -->
      <div class="d-flex justify-cente">
        <ScanBlock />
      </div>

      <!-- 告警类型列表 -->
      <div class="member-block flex-grow-1">
        <div class="member-scroll-wrapper">
          <div class="member-grid">
            <div v-for="(item, index) in list" :key="item.alarmCode" class="member-item animate-in"
              :style="{ '--delay': index * 100 + 'ms' }">
              <div class="d-flex align-baseline justify-space-between">
                <TransitionNumber class="member-value" :value="item.count" />
                <span class="text-grey-darken-1">条</span>
              </div>
              <div class="member-progress mt-2">
                <div v-for="(c, i) in getColors(item.alarmLevel)" :key="i" class="progress-dash"
                  :class="{ active: i < getProgressCount(item.count) }"
                  :style="{ '--active-color': c, '--dash-delay': i * 20 + 'ms' }">
                </div>
              </div>
              <div class="member-label mt-2">{{ item.alarmName }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import TransitionNumber from '@/components/TransitionNumber/index.vue'
import ScanBlock from './scan-block/index.vue'
import { getAlarmTypeCount } from '@/api/alarm'
import dayjs from 'dayjs'

const list = ref([])
const total = ref(0)
const max = ref(6000)

const percentage = computed(() => {
  return Math.min(((total.value / max.value) * 100), 100).toFixed(2)
})

// 告警级别颜色映射
const Colors = {
  1: ['#94A3B8', '#CBD5E1'],
  2: ['#4ABE65', '#4FFFD6'],
  3: ['#3B82F6', '#60A5FA'],
  4: ['#F67A3C', '#F6D41D'],
  5: ['#E3001F', '#FF4D6A'],
}

function getColors(level) {
  const colorPair = Colors[level] || Colors[3]
  const colors = []
  for (let i = 0; i < 20; i++) {
    const ratio = i / 19
    colors.push(interpolateColor(colorPair[0], colorPair[1], ratio))
  }
  return colors
}

function interpolateColor(color1, color2, ratio) {
  const hex = (color) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color)
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null
  }

  const c1 = hex(color1)
  const c2 = hex(color2)

  if (!c1 || !c2) return color1

  const r = Math.round(c1.r + (c2.r - c1.r) * ratio)
  const g = Math.round(c1.g + (c2.g - c1.g) * ratio)
  const b = Math.round(c1.b + (c2.b - c1.b) * ratio)

  return `rgb(${r}, ${g}, ${b})`
}

function getProgressCount(count) {
  return Math.round((Math.min(count, 1000) / 1000) * 20)
}

async function fetchData() {
  try {
    const res = await getAlarmTypeCount({
      startTime: dayjs().subtract(1, 'day').startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      endTime: dayjs().subtract(1, 'day').endOf('day').format('YYYY-MM-DD HH:mm:ss'),
    })

    // web-ui 的 request 拦截器直接返回了业务数据数组或对象
    const data = res || []
    list.value = Array.isArray(data) ? data : (data.data || [])
    total.value = list.value.reduce((acc, current) => acc + (current.count || 0), 0)

    // 动态调整最大值以保证进度条比例美观
    if (total.value > max.value) {
      max.value = total.value * 1.2
    }
  } catch (error) {
    console.error('Failed to fetch alarm type count:', error)
  }
}

onMounted(() => {
  fetchData()
})
</script>

<style scoped lang="scss">
.total-alarm {
  :deep(.v-card-item) {
    padding: 24px 28px 10px 28px !important;

    .v-card-title {
      font-weight: 400 !important;
      font-size: 20px !important;
    }
  }
}

.card-bg {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.63) 0%, rgba(255, 255, 255, 0.42) 104.73%);
}

.card-bd {
  border: 1px solid #fff;
}

.total-block {
  padding: 16px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 12px;
}

.total-info {
  font-size: 14px;
}

.total-value {
  font-size: 32px;
  font-weight: 700;
  color: #3A68FF;
}

.progress-bar {
  height: 10px;
  background: #E5E7EB;
  border-radius: 5px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(to right, #3A68FF, #60A5FA);
  border-radius: 5px;
  transition: width 1s cubic-bezier(0.34, 1.56, 0.64, 1);
  width: 0;
}

/* 扫描动画 styles */
.scan-container {
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.radar {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  border: 1px solid rgba(58, 104, 255, 0.2);
  position: relative;
  background: radial-gradient(circle, rgba(58, 104, 255, 0.05) 0%, transparent 70%);

  .sweep {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: conic-gradient(from 0deg, rgba(58, 104, 255, 0.4) 0deg, transparent 90deg);
    animation: rotate 3s linear infinite;
  }

  .circles {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    .circle {
      position: absolute;
      border-radius: 50%;
      border: 1px solid rgba(58, 104, 255, 0.1);
      animation: pulse 2s infinite;

      &.c1 {
        width: 30%;
        height: 30%;
        animation-delay: 0s;
      }

      &.c2 {
        width: 60%;
        height: 60%;
        animation-delay: 0.5s;
      }

      &.c3 {
        width: 90%;
        height: 90%;
        animation-delay: 1s;
      }
    }
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@keyframes pulse {
  0% {
    transform: scale(0.8);
    opacity: 0.5;
  }

  50% {
    transform: scale(1.1);
    opacity: 0.1;
  }

  100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
}

.member-block {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.member-scroll-wrapper {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 8px;
}

.member-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.member-item {
  padding: 12px;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 8px;
  opacity: 0;
  transform: translateY(20px);
}

.member-item.animate-in {
  animation: slideIn 0.5s ease forwards;
  animation-delay: var(--delay);
}

@keyframes slideIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.member-value {
  font-size: 20px;
  font-weight: 600;
  color: #374151;
}

.member-progress {
  display: flex;
  gap: 2px;
}

.progress-dash {
  flex: 1;
  height: 6px;
  background: #E5E7EB;
  border-radius: 2px;
  transition: background 0.3s ease;
}

.progress-dash.active {
  animation: dashEnter 0.4s ease forwards;
  animation-delay: var(--dash-delay);
}

@keyframes dashEnter {
  from {
    background: #E5E7EB;
  }

  to {
    background: var(--active-color, #3B82F6);
  }
}

.member-label {
  font-size: 12px;
  color: #6B7280;
}
</style>
